import { useCallback, useEffect, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';

// useQuestChain — what this player can see right now, refreshable.
//
// Quests are PULL-based by design: nothing pushes when a field is written or a
// track lands. So every surface that shows quest state needs the same two
// things — the list, and a way to re-ask after something happened. Shared so the
// phone and the admin panel can never disagree about whether an act is done.
export function useQuestChain(avatarId, { enabled = true } = {}) {
  const [quests, setQuests] = useState([]);
  const [error, setError] = useState('');
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!enabled || !avatarId) return [];
    try {
      const res = await fetch(`${API_BASE}/api/quests?avatarId=${avatarId}`, {
        headers: { Authorization: `Bearer ${AUTH_TOKEN}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `quests ${res.status}`);
      const list = data.quests || [];
      setQuests(list); setError(''); setLoaded(true);
      return list;
    } catch (err) {
      setError(err.message || 'Could not load quests');
      setLoaded(true);
      return [];
    }
  }, [avatarId, enabled]);

  useEffect(() => { refresh(); }, [refresh]);

  // A quest that has been COMPLETED drops out of `availableFor` entirely
  // (non-repeatables are filtered server-side), so "not in the list" and "not
  // yet reachable" are the same shape here. Callers that need to tell them apart
  // ask about chain position, not about presence.
  const statusOf = useCallback(
    (slug) => quests.find((q) => q.slug === slug)?.status || null,
    [quests]
  );

  return { quests, statusOf, refresh, error, loaded };
}

export default useQuestChain;
